import type { Shadows } from '@mui/material/styles/shadows'

/**
 * Elevation is built as a ramp rather than hand-written per level, so the steps
 * stay proportional to each other.
 *
 * Each level is two layers: a tight ambient shadow that seats the element on the
 * surface, and a softer directional one that carries the actual lift. A single
 * flat blur — what the previous ramp used from level 2 upward — reads as a haze
 * around the element rather than as height above the page.
 *
 * The light ramp is tinted warm rather than neutral grey; against the brand
 * orange a cool grey shadow reads as a slightly dirty edge.
 */
function buildShadows(rgb: string, ambient: number, direct: number): Shadows {
  return Array.from({ length: 25 }, (_, level) => {
    if (level === 0) return 'none'

    const y = Math.max(1, Math.round(level * 0.75))
    const blur = Math.max(2, Math.round(level * 1.7))
    const spread = -Math.round(level * 0.18)

    const ambientY = Math.max(1, Math.round(y / 2))
    const ambientBlur = Math.max(1, Math.round(blur / 2.5))

    return [
      `0px ${ambientY}px ${ambientBlur}px rgba(${rgb}, ${ambient})`,
      `0px ${y}px ${blur}px ${spread}px rgba(${rgb}, ${direct})`,
    ].join(', ')
  }) as unknown as Shadows
}

export const lightShadows = buildShadows('41, 37, 36', 0.05, 0.09)

export const darkShadows = buildShadows('0, 0, 0', 0.28, 0.4)

// High contrast draws separation with explicit borders, not depth. Every level
// here was previously a full-opacity white glow, which haloed every card, menu
// and dialog and worked against the edge definition this theme exists to give.
export const highContrastShadows = Array.from({ length: 25 }, () => 'none') as unknown as Shadows
